import React, { useState, useEffect } from 'react';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Chip from '@mui/material/Chip';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { Divider, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { Alert } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import CommentIcon from '@mui/icons-material/Comment';
import { APPOINTMENTS_ENDPOINT } from '../../config/urls';

// Filter buttons
import ButtonGroup from '@mui/material/ButtonGroup';

const columns = [
  { id: 'appointment_id', label: 'Appointment ID' },
  { id: 'client_name', label: 'Client Name' },
  { id: 'phone', label: 'Phone' },
  { id: 'date', label: 'Date' },
  { id: 'time', label: 'Time' },
  { id: 'property_type', label: 'Property Type' },
  { id: 'agent', label: 'Agent' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions' },
];

export default function AppointmentManagementTable({ refreshKey, searchQuery }) {
  // State for appointments data
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [filteredStatus, setFilteredStatus] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  // State for modals
  const [openModal, setOpenModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertSeverity, setAlertSeverity] = useState('success');

  // Function to get the token (replace with your authentication method)
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Function to format date to day, month date, year
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    // Create a date object with the time string
    // We use a dummy date because we only require the time portion
    const dateObj = new Date(`2000-01-01T${timeString}`);
    
    // Use toLocaleTimeString to format the time in 12-hour format with AM/PM
    return dateObj.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  // Function to fetch appointments data
  const fetchAppointmentsData = async () => {
    const token = getToken();
    let endpoint = `${APPOINTMENTS_ENDPOINT}/appointments?limit=${rowsPerPage}&offset=${page * rowsPerPage}`;
    
    if (filteredStatus !== 'all') {
      endpoint += `&status=${filteredStatus}`;
    }
    
    if (searchQuery) {
      endpoint += `&search=${searchQuery}`;
    }

    try {
      const response = await fetch(endpoint, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": localStorage.getItem('tenant')
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch appointments data');
      }
      
      const jsonData = await response.json();
      setAppointmentsData(jsonData.results || []);
      setTotalCount(jsonData.total_count || 0);
      setPendingCount(jsonData.pending_count || 0);
      setConfirmedCount(jsonData.confirmed_count || 0);
      setCancelledCount(jsonData.cancelled_count || 0);
    } catch (error) {
      console.error('Error fetching appointments data:', error.message);
      setAlertMessage('Failed to load appointments. Please try again.');
      setAlertSeverity('error');
    }
  };

  // Function to update appointment status
  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    const token = getToken();
    const endpoint = `${APPOINTMENTS_ENDPOINT}/appointments/${appointmentId}/${newStatus.toLowerCase()}/`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": localStorage.getItem('tenant')
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${newStatus.toLowerCase()} appointment`);
      }
      
      // Close modal and refresh data
      setOpenModal(false);
      fetchAppointmentsData();
      if (newStatus.toLowerCase() === 'confirm') {
        setAlertMessage(`Appointment confirmed successfully`);
      }
      else if (newStatus.toLowerCase() === 'cancel') {
        setAlertMessage(`Appointment cancelled successfully`);
      }
      else {
        setAlertMessage(`Appointment status updated successfully`);
      }
      setAlertSeverity('success');
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error(`Error ${newStatus.toLowerCase()} appointment:`, error.message);
      setAlertMessage(`Failed to ${newStatus.toLowerCase()} appointment. Please try again.`);
      setAlertSeverity('error');
    }
  };

  // Handle status filter change
  const handleFilterChange = (status) => {
    setFilteredStatus(status);
    setPage(0); // Reset to first page when filter changes
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open modal with appointment details
  const handleOpenModal = (appointment) => {
    setSelectedAppointment(appointment);
    setOpenModal(true);
  };

  // Close the modal
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Confirm appointment from table
  const handleConfirmAppointment = (appointmentId, event) => {
    event.stopPropagation();
    updateAppointmentStatus(appointmentId, 'confirm');
  };

  // Cancel appointment from table
  const handleCancelAppointment = (appointmentId, event) => {
    event.stopPropagation();
    updateAppointmentStatus(appointmentId, 'cancel');
  };

  // Fetch data when dependencies change
  useEffect(() => {
    fetchAppointmentsData();
  }, [page, rowsPerPage, filteredStatus, searchQuery, refreshKey]);

  // Modal style
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

  return (
    <Box sx={{ position: 'relative' }}>
        {alertMessage && (
          <Box
          sx={{
            position: 'absolute',
            top: '-42px',     // Adjust depending on actual header + search margin
            left: '280px',    // Align with the end of search input
            zIndex: 999,
          }}
        >
            <Alert
            severity={alertSeverity}
            variant="filled"
            onClose={() => setAlertMessage(null)}
            sx={{
              height: '40px',
              alignItems: 'center',
              px: 2,
              minWidth: '300px',
            }}
          >
            {alertMessage}
          </Alert>
          </Box>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
            <Paper 
                elevation={0} 
                sx={{ 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1 
                }}
            >
                <Typography variant="subtitle2" color="text.secondary">
                Total Appointments
                </Typography>
                <Typography variant="h4" component="div" sx={{ mt: 1 }}>
                {totalCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                All scheduled appointments
                </Typography>
            </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
            <Paper 
                elevation={0} 
                sx={{ 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                bgcolor: 'rgba(255, 235, 59, 0.1)'
                }}
            >
                <Typography variant="subtitle2" color="text.secondary">
                Pending
                </Typography>
                <Typography variant="h4" component="div" sx={{ mt: 1 }}>
                {pendingCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Awaiting confirmation
                </Typography>
            </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
            <Paper 
                elevation={0} 
                sx={{ 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                bgcolor: 'rgba(76, 175, 80, 0.1)'
                }}
            >
                <Typography variant="subtitle2" color="text.secondary">
                Confirmed
                </Typography>
                <Typography variant="h4" component="div" sx={{ mt: 1 }}>
                {confirmedCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Ready to proceed
                </Typography>
            </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
            <Paper 
                elevation={0} 
                sx={{ 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                bgcolor: 'rgba(244, 67, 54, 0.1)'
                }}
            >
                <Typography variant="subtitle2" color="text.secondary">
                Cancelled
                </Typography>
                <Typography variant="h4" component="div" sx={{ mt: 1 }}>
                {cancelledCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                No longer scheduled
                </Typography>
            </Paper>
            </Grid>
        </Grid>

        {/* Filter Buttons */}
        <ButtonGroup 
            variant="outlined" 
            aria-label="filter appointments"
            sx={{ mb: 2 }}
        >
            <Button 
            variant={filteredStatus === 'all' ? 'contained' : 'outlined'}
            onClick={() => handleFilterChange('all')}
            >
            All
            </Button>
            <Button 
            variant={filteredStatus === 'pending' ? 'contained' : 'outlined'}
            onClick={() => handleFilterChange('pending')}
            >
            Pending
            </Button>
            <Button 
            variant={filteredStatus === 'confirmed' ? 'contained' : 'outlined'}
            onClick={() => handleFilterChange('confirmed')}
            >
            Confirmed
            </Button>
            <Button 
            variant={filteredStatus === 'cancelled' ? 'contained' : 'outlined'}
            onClick={() => handleFilterChange('cancelled')}
            >
            Cancelled
            </Button>
        </ButtonGroup>

        {/* Appointments Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
                <TableHead>
                <TableRow>
                    {columns.map((column) => (
                    <TableCell
                        key={column.id}
                        align="left"
                        style={{ minWidth: column.minWidth, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                        {column.label}
                    </TableCell>
                    ))}
                </TableRow>
                </TableHead>
                <TableBody>
                {appointmentsData.length === 0 ? (
                    <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                        No appointments found
                    </TableCell>
                    </TableRow>
                ) : (
                    appointmentsData.map((appointment) => (
                    <TableRow 
                        hover 
                        role="checkbox" 
                        tabIndex={-1} 
                        key={appointment.id}
                        onClick={() => handleOpenModal(appointment)}
                        sx={{ cursor: 'pointer' }}
                    >
                        <TableCell>APT-{appointment.id}</TableCell>
                        <TableCell>{appointment.client_name}</TableCell>
                        <TableCell>{appointment.phone_no}</TableCell>
                        <TableCell>{formatDate(appointment.date)}</TableCell>
                        <TableCell>{formatTime(appointment.from_ts)} - {formatTime(appointment.to_ts)}</TableCell>
                        <TableCell>{appointment.property_type_display}</TableCell>
                        <TableCell>{appointment.agent_name || 'Not Assigned'}</TableCell>
                        <TableCell>
                        <Chip 
                            label={appointment.appointment_status} 
                            color={
                            appointment.appointment_status === 'Confirmed' ? 'success' :
                            appointment.appointment_status === 'Cancelled' ? 'error' : 'warning'
                            }
                            variant="outlined"
                            size="small"
                        />
                        </TableCell>
                        <TableCell>
                        <IconButton 
                            color="primary" 
                            aria-label="view details"
                            onClick={(e) => {
                            e.stopPropagation();
                            handleOpenModal(appointment);
                            }}
                        >
                            <VisibilityIcon />
                        </IconButton>
                        
                        {appointment.status !== 'Confirmed' && (
                            <IconButton 
                            color="success" 
                            aria-label="confirm appointment"
                            onClick={(e) => handleConfirmAppointment(appointment.id, e)}
                            >
                            <CheckIcon />
                            </IconButton>
                        )}
                        
                        {appointment.status !== 'Cancelled' && (
                            <IconButton 
                            color="error" 
                            aria-label="cancel appointment"
                            onClick={(e) => handleCancelAppointment(appointment.id, e)}
                            >
                            <CloseIcon />
                            </IconButton>
                        )}
                        </TableCell>
                    </TableRow>
                    ))
                )}
                </TableBody>
            </Table>
            </TableContainer>
            <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={filteredStatus === 'all' ? totalCount : 
                    filteredStatus === 'pending' ? pendingCount :
                    filteredStatus === 'confirmed' ? confirmedCount : cancelledCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>

        {/* Appointment Details Modal */}
        {selectedAppointment && (
            <Modal
            open={openModal}
            onClose={handleCloseModal}
            aria-labelledby="appointment-details-modal"
            aria-describedby="modal-showing-appointment-details"
            >
            <Box sx={modalStyle}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    Appointment Details
                </Typography>
                <IconButton onClick={handleCloseModal} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
                </Box>

                <Divider />
                
                <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">APT-{selectedAppointment.id}</Typography>
                <Chip 
                    label={selectedAppointment.appointment_status} 
                    color={
                    selectedAppointment.status === 'Confirmed' ? 'success' :
                    selectedAppointment.status === 'Cancelled' ? 'error' : 'warning'
                    }
                    size="small"
                />
                </Box>

                <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                    Client Information
                </Typography>
                <Box sx={{ ml: 4 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Name:</strong> {selectedAppointment.client_name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Email:</strong> {selectedAppointment.client_email || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                    <strong>Phone:</strong> {selectedAppointment.phone_no}
                    </Typography>
                </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventIcon fontSize="small" sx={{ mr: 1 }} />
                    Appointment Information
                </Typography>
                <Box sx={{ ml: 4 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Date:</strong> {formatDate(selectedAppointment.date)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Time:</strong> {formatTime(selectedAppointment.from_ts)} - {formatTime(selectedAppointment.to_ts)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Property Type:</strong> {selectedAppointment.property_type_display}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Agent:</strong> {selectedAppointment.agent_name || 'Not Assigned'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Created:</strong> {new Date(selectedAppointment.created_at).toLocaleDateString('en-GB') + ' ' + new Date(selectedAppointment.created_at).toLocaleTimeString()}
                    </Typography>
                </Box>
                </Box>

                {selectedAppointment.notes && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CommentIcon fontSize="small" sx={{ mr: 1 }} />
                    Additional Information
                    </Typography>
                    <Paper 
                    variant="outlined" 
                    sx={{ p: 2, ml: 4, bgcolor: 'background.default' }}
                    >
                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                        {selectedAppointment.notes}
                    </Typography>
                    </Paper>
                </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                {selectedAppointment.status !== 'Confirmed' && (
                    <Button 
                    variant="contained" 
                    color="success"
                    onClick={() => updateAppointmentStatus(selectedAppointment.id, 'confirm')}
                    >
                    Confirm Appointment
                    </Button>
                )}
                
                {selectedAppointment.status !== 'Cancelled' && (
                    <Button 
                    variant="contained" 
                    color="error"
                    onClick={() => updateAppointmentStatus(selectedAppointment.id, 'cancel')}
                    >
                    Cancel Appointment
                    </Button>
                )}
                
                <Button 
                    variant="contained"
                    onClick={handleCloseModal}
                >
                    Close
                </Button>
                </Box>
            </Box>
        </Modal>
        )}
    </Box>
  );
}